import { Button, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'

import {
  fetchAtHomeSupportContact,
  updateAtHomeSupportContact
} from 'api/atHome'

/**
 * The WhatsApp number the parent app's help icon opens.
 *
 * SUPER ADMINS ONLY, and the gate is the BACKEND's. `editable` comes down in
 * the GET, so this screen disables the field from the server's own answer
 * rather than re-deriving "is this a super admin" in the browser -- a check
 * duplicated in two places is a check that will eventually disagree with
 * itself. A non-super-admin sees the number (useful) and cannot change it;
 * the PUT is refused with 403 regardless of what the UI allows.
 *
 * One field, no list: there is exactly one number per environment, and dev
 * and prod are separate databases, so what you see here is what parents on
 * THIS environment reach.
 */
const useStyles = makeStyles((theme) => ({
  wrap: { padding: theme.spacing(3), maxWidth: 560 },
  row: { display: 'flex', gap: theme.spacing(2), alignItems: 'flex-start' },
  note: { marginTop: theme.spacing(1), color: theme.palette.text.secondary },
  saved: { marginTop: theme.spacing(1), color: theme.palette.success.main },
  error: { marginTop: theme.spacing(1), color: theme.palette.error.main }
}))

/**
 * Show the local 10 digits, not the stored 919405222555. Storage stays
 * canonical because a wa.me link needs the country code; this is only what the
 * box reads back. The 91 comes off ONLY when exactly ten digits remain, so a
 * number on another country code is left alone.
 */
const toLocal = (stored) => {
  const d = String(stored || '').replace(/\D/g, '')
  return d.length === 12 && d.startsWith('91') ? d.slice(2) : d
}

const AtHomeSupportContact = () => {
  const classes = useStyles()
  const [number, setNumber] = useState('')
  const [message, setMessage] = useState('')
  const [messageDefault, setMessageDefault] = useState('')
  const [editable, setEditable] = useState(false)
  const [envDefault, setEnvDefault] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let off = false
    fetchAtHomeSupportContact()
      .then((d) => {
        if (off || !d) return
        setNumber(toLocal(d.whatsapp_number))
        setMessage(d.message || '')
        setMessageDefault(d.message_default || '')
        setEditable(Boolean(d.editable))
        setEnvDefault(d.environment_default || '')
      })
      .catch(() => setError('Could not load the support number.'))
      .finally(() => !off && setLoading(false))
    return () => {
      off = true
    }
  }, [])

  const save = () => {
    setSaving(true)
    setSaved(false)
    setError('')
    updateAtHomeSupportContact({ whatsapp_number: number, message })
      .then((d) => {
        if (d && d.whatsapp_number) setNumber(toLocal(d.whatsapp_number))
        if (d && d.message) setMessage(d.message)
        setSaved(true)
      })
      .catch((e) => {
        // The backend refuses an unreadable number with a message worth
        // showing; anything else is a permission problem.
        const detail =
          e?.response?.data?.detail ||
          e?.response?.data?.[0] ||
          'Could not save. Only a super admin can change this.'
        setError(String(detail))
      })
      .finally(() => setSaving(false))
  }

  if (loading) return <div className={classes.wrap}>Loading…</div>

  return (
    <div className={classes.wrap}>
      <Typography variant="h6">Support WhatsApp</Typography>
      <Typography variant="body2" className={classes.note}>
        The number the help icon opens in the At-Home parent app, and the
        message it opens with. These are for <strong>this environment</strong>{' '}
        only.
      </Typography>

      <div style={{ marginTop: 16 }}>
        <TextField
          label="WhatsApp number"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          disabled={!editable || saving}
          placeholder="9405222555"
          helperText={
            editable
              ? 'Just the 10 digits. +91 is added for you.'
              : 'Only a super admin can change this.'
          }
          variant="outlined"
          size="small"
          fullWidth
        />
      </div>

      {/* The greeting the chat opens with. Screens that know what the parent
          is stuck on (a trial, a slot) send their own line instead, so this is
          the DEFAULT, not every message the desk receives. */}
      <div style={{ marginTop: 16 }}>
        <TextField
          label="Opening message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={!editable || saving}
          placeholder={messageDefault}
          multiline
          minRows={2}
          inputProps={{ maxLength: 300 }}
          helperText={
            editable
              ? 'What the chat is pre-filled with when a parent taps the help icon. Leave blank for the default.'
              : 'Only a super admin can change this.'
          }
          variant="outlined"
          size="small"
          fullWidth
        />
      </div>

      <div className={classes.row} style={{ marginTop: 16 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          disabled={!editable || saving || !number.trim()}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {envDefault ? (
        <Typography variant="caption" className={classes.note}>
          Falls back to {envDefault} if this is ever cleared.
        </Typography>
      ) : null}
      {saved ? (
        <Typography variant="body2" className={classes.saved}>
          Saved. The parent app picks this up on its next launch.
        </Typography>
      ) : null}
      {error ? (
        <Typography variant="body2" className={classes.error}>
          {error}
        </Typography>
      ) : null}
    </div>
  )
}

export default AtHomeSupportContact
